import type { MockedResponse } from "@apollo/client/testing";
import { COMMISSION_PROGRAM_CONSENTS_QUERY } from "../consents/queries";
import {
  COMMISSION_ALLOCATION_AGREEMENT_LIVE_QUERY,
  COMMISSION_ALLOCATION_AGREEMENTS_QUERY
} from "../allocations/queries";
import type { CommissionAllocationAgreementNode } from "../allocations/types";
import type { CommissionProgramConsentNode } from "../consents/types";
import { buildConnection } from "./connection";
import { createCatalogMock, withTypenames, type MockResolver } from "./mockUtils";
import { runtimeAllocationAgreements, runtimeCounters, runtimeProgramConsents } from "./runtimeState";

function buildConsentsMockData(campaignId?: string, crmPersonId?: string) {
  const nodes = runtimeProgramConsents
    .filter((consent) => {
      const campaignMatch = !campaignId || consent.commissionCampaignId === campaignId;
      const personMatch = !crmPersonId || consent.crmPersonId === crmPersonId;
      return campaignMatch && personMatch;
    })
    .map((node) => withTypenames(node, "BackofficeCommissionProgramConsent"));

  return {
    commissionProgramConsents: buildConnection("BackofficeCommissionProgramConsentConnection", nodes)
  };
}

function toMockAgreement(agreement: (typeof runtimeAllocationAgreements)[number]) {
  return {
    __typename: "BackofficeCommissionAllocationAgreement",
    ...agreement,
    splits: agreement.splits.map((split) => ({
      __typename: "BackofficeCommissionAllocationSplit",
      ...split
    }))
  };
}

function findAllocationAgreement(receiverCrmPersonId: string, campaignId: string) {
  return runtimeAllocationAgreements.find(
    (node) => node.receiverCrmPersonId === receiverCrmPersonId && node.commissionCampaignId === campaignId
  );
}

function buildAllocationAgreementLiveMockData(receiverCrmPersonId: string, campaignId: string) {
  // Dev-mock leniency: the real Live root is active-only with asOf coverage,
  // but mocks return the draft-or-active row so edit flows keep working.
  const agreement = findAllocationAgreement(receiverCrmPersonId, campaignId);

  if (!agreement) {
    return { commissionAllocationAgreementLive: null };
  }

  return { commissionAllocationAgreementLive: toMockAgreement(agreement) };
}

function buildAllocationAgreementsMockData(receiverCrmPersonId: string, campaignId?: string, status?: string) {
  const nodes = runtimeAllocationAgreements
    .filter((node) => {
      const receiverMatch = node.receiverCrmPersonId === receiverCrmPersonId;
      const campaignMatch = !campaignId || node.commissionCampaignId === campaignId;
      const statusMatch = !status || node.status === status;
      return receiverMatch && campaignMatch && statusMatch;
    })
    .map(toMockAgreement);

  return {
    commissionAllocationAgreements: {
      __typename: "BackofficeCommissionAllocationAgreementConnection",
      nodes,
      totalCount: nodes.length
    }
  };
}

export const commissionProgramConsentsApolloMock = createCatalogMock(
  COMMISSION_PROGRAM_CONSENTS_QUERY,
  buildConsentsMockData(undefined, "person-ada")
);

export const commissionAllocationAgreementLiveApolloMock = createCatalogMock(
  COMMISSION_ALLOCATION_AGREEMENT_LIVE_QUERY,
  buildAllocationAgreementLiveMockData("person-ada", "commission-campaign-2026-acme")
);

export const commissionAllocationAgreementsApolloMock = createCatalogMock(
  COMMISSION_ALLOCATION_AGREEMENTS_QUERY,
  buildAllocationAgreementsMockData("person-ada")
);

export const consentsApolloMocks: MockedResponse[] = [
  commissionProgramConsentsApolloMock,
  commissionAllocationAgreementLiveApolloMock,
  commissionAllocationAgreementsApolloMock
];

export const consentsVariableMockResolvers: Record<string, MockResolver> = {
  CommissionProgramConsents: (variables) =>
    buildConsentsMockData(
      variables.campaignId ? String(variables.campaignId) : undefined,
      variables.crmPersonId ? String(variables.crmPersonId) : undefined
    ),
  CommissionProgramConsent: (variables) => {
    const consent = runtimeProgramConsents.find((node) => node.id === String(variables.id));

    return {
      commissionProgramConsent: consent ? withTypenames(consent, "BackofficeCommissionProgramConsent") : null
    };
  },
  CreateCommissionProgramConsent: (variables) => {
    runtimeCounters.consent += 1;
    const input = variables.input as {
      commissionCampaignId: string;
      crmPersonId: string;
      attributionModel: CommissionProgramConsentNode["attributionModel"];
      ownership?: CommissionProgramConsentNode["ownership"];
      startsAt: string;
      endsAt?: string | null;
      status: CommissionProgramConsentNode["status"];
    };
    const consent: CommissionProgramConsentNode = {
      id: `commission-consent-runtime-${runtimeCounters.consent}`,
      commissionCampaignId: String(input.commissionCampaignId),
      crmPersonId: String(input.crmPersonId),
      attributionModel: input.attributionModel,
      ownership: input.ownership,
      startsAt: input.startsAt,
      endsAt: input.endsAt ?? null,
      status: input.status
    };
    runtimeProgramConsents.push(consent);

    return {
      commissionCreateProgramConsent: {
        consent: withTypenames(consent, "BackofficeCommissionProgramConsent")
      }
    };
  },
  UpdateCommissionProgramConsent: (variables) => {
    const input = variables.input as Partial<CommissionProgramConsentNode> & { id: string };
    const index = runtimeProgramConsents.findIndex((consent) => consent.id === input.id);
    const existing = index >= 0 ? runtimeProgramConsents[index] : runtimeProgramConsents[0];
    const updated: CommissionProgramConsentNode = {
      ...existing,
      ...input,
      id: String(input.id)
    };
    if (index >= 0) {
      runtimeProgramConsents[index] = updated;
    }

    return {
      commissionUpdateProgramConsent: {
        consent: withTypenames(updated, "BackofficeCommissionProgramConsent")
      }
    };
  },
  CommissionAllocationAgreementLive: (variables) =>
    buildAllocationAgreementLiveMockData(String(variables.receiverCrmPersonId), String(variables.campaignId)),
  CommissionAllocationAgreements: (variables) =>
    buildAllocationAgreementsMockData(
      String(variables.receiverCrmPersonId),
      variables.campaignId ? String(variables.campaignId) : undefined,
      variables.status ? String(variables.status) : undefined
    ),
  UpsertCommissionAllocationAgreement: (variables) => {
    const input = variables.input as {
      receiverCrmPersonId: string;
      commissionCampaignId: string;
      crmFacilityId?: string | null;
      facilityName: string;
      status: CommissionAllocationAgreementNode["status"];
      effectiveStart: string;
      splits: CommissionAllocationAgreementNode["splits"];
    };

    const agreement: CommissionAllocationAgreementNode = {
      id: `commission-iaa-runtime-${runtimeAllocationAgreements.length + 1}`,
      commissionCampaignId: String(input.commissionCampaignId),
      receiverCrmPersonId: String(input.receiverCrmPersonId),
      crmFacilityId: input.crmFacilityId ?? null,
      facilityName: input.facilityName,
      status: input.status,
      effectiveStart: input.effectiveStart,
      effectiveEnd: null,
      splits: input.splits.map((split, index) => ({
        id: `commission-split-runtime-${index + 1}`,
        crmPersonId: split.crmPersonId,
        pgaId: split.pgaId,
        displayName: split.displayName,
        splitPercent: split.splitPercent,
        hasActiveFaa: split.pgaId === "12345678" || split.crmPersonId === "person-ada"
      }))
    };

    const existingIndex = runtimeAllocationAgreements.findIndex(
      (node) =>
        node.receiverCrmPersonId === agreement.receiverCrmPersonId &&
        node.commissionCampaignId === agreement.commissionCampaignId
    );
    if (existingIndex >= 0) {
      runtimeAllocationAgreements[existingIndex] = { ...agreement, id: runtimeAllocationAgreements[existingIndex].id };
    } else {
      runtimeAllocationAgreements.push(agreement);
    }

    const saved = existingIndex >= 0 ? runtimeAllocationAgreements[existingIndex] : agreement;

    return {
      commissionUpsertAllocationAgreement: {
        agreement: toMockAgreement(saved)
      }
    };
  }
};
